/*
 * Keyboard/viewport geometry, kept pure so it can be unit-tested without a
 * layout engine — the same approach as scrollStick.js, which this builds on
 * rather than replaces.
 *
 * WHY VisualViewport rather than window height:
 *   iOS Safari does NOT resize the layout viewport when the keyboard opens —
 *   window.innerHeight is unchanged, so anything positioned from it ends up
 *   underneath the keyboard. Android Chrome does resize it (and with
 *   interactive-widget=resizes-content it resizes the content box too).
 *   visualViewport reports the actually-visible rectangle on both, so it is the
 *   only signal that works across the two.
 */
import { NEAR_BOTTOM_PX } from './scrollStick';

/*
 * Below this many pixels we treat the keyboard as absent. Two reasons:
 *  - iPad floating and split keyboards are DETACHED from the bottom edge, so
 *    they must not make the composer reserve space (that is the "large empty
 *    gap" failure). A floating keyboard leaves visualViewport.height close to
 *    the layout height, so it lands under this threshold and reads as 0.
 *  - Mobile browser chrome (collapsing URL bars) jitters the visual viewport by
 *    a few dozen pixels; without a floor the composer would twitch while
 *    scrolling.
 */
export const MIN_KEYBOARD_PX = 80;

/**
 * Pixels of the layout viewport currently covered at the bottom — i.e. how far
 * up the composer must move to sit just above the keyboard.
 *
 * offsetTop matters because iOS scrolls the visual viewport within the layout
 * viewport when focusing an input near the bottom; ignoring it double-counts
 * and pushes the composer too high.
 */
export function keyboardInset({ layoutHeight, viewportHeight, offsetTop = 0 }) {
  if (!Number.isFinite(layoutHeight) || !Number.isFinite(viewportHeight)) return 0;
  const inset = layoutHeight - (viewportHeight + (Number.isFinite(offsetTop) ? offsetTop : 0));
  if (!Number.isFinite(inset) || inset < MIN_KEYBOARD_PX) return 0;
  // Never exceed the layout height; a bogus reading must not fling the composer
  // off-screen.
  return Math.round(Math.min(inset, layoutHeight));
}

/**
 * Whether to offer the "scroll to latest" affordance: the user has scrolled up
 * (unpinned) AND there is meaningful content below the fold. Reuses
 * scrollStick's NEAR_BOTTOM_PX so the button can never disagree with the pin
 * logic about what "near the bottom" means.
 */
export function showJumpToLatest({ pinned, scrollY, scrollHeight, innerHeight }) {
  if (pinned) return false;
  const distanceFromBottom = scrollHeight - innerHeight - scrollY;
  return distanceFromBottom > NEAR_BOTTOM_PX;
}

/**
 * Scroll offset that keeps the user's position stable across a viewport change
 * (rotation, resize, keyboard open/close) by preserving their DISTANCE FROM THE
 * BOTTOM rather than their absolute offset. In a bottom-anchored chat the
 * content below you is what you care about; preserving the absolute offset
 * would shift the conversation under you when the height changes.
 */
export function preservedScrollTop({ distanceFromBottom, scrollHeight, innerHeight }) {
  const max = Math.max(0, scrollHeight - innerHeight);
  if (!Number.isFinite(distanceFromBottom)) return max;
  return Math.max(0, Math.min(max, max - distanceFromBottom));
}
